import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n.jsx';
import { useOutletContext } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';

// Wrap a module's tabs-bar contextual controls (filters, search boxes, Add/Save/CSV buttons) with
// this so they move out of the module's own tabs row on mobile. Layout.jsx mounts a portal slot in
// the header and exposes its DOM node as `headerActionsEl` via useOutletContext only on mobile -- on
// desktop (or before the slot exists) this is null, so children render exactly where they're placed
// in the module's own JSX, completely unchanged.
//
// On mobile the controls don't render directly in the header either -- desktop-sized dropdowns and
// search boxes routinely overflowed the header and pushed the sidebar toggle off-screen. Instead they
// collapse behind a single icon button; tapping it opens a panel that slides in from the right, with
// the actual controls at full, comfortable width. This is universal to every module that uses this
// wrapper -- no per-module changes needed here or in any of the files already using it.
const DRAG_CLOSE_THRESHOLD = 80;
const TRANSITION_MS = 220;

const HeaderActionsPortal = ({ children }) => {
  const { t } = useT();
  const { headerActionsEl } = useOutletContext();
  const [open, setOpen] = useState(false); // mounted at all
  const [entered, setEntered] = useState(false); // drives the slide-in/out transform
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const startXRef = useRef(0);

  if (!headerActionsEl) return children;

  const openDrawer = () => {
    setOpen(true);
    // Mount at translateX(100%) first, then flip to 0 on the next frame so the transition actually
    // has a "from" state to animate out of, instead of just appearing already open.
    requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
  };

  const closeDrawer = () => {
    setEntered(false);
    setDragging(false);
    setDragX(0);
    setTimeout(() => setOpen(false), TRANSITION_MS);
  };

  // Swipe-to-dismiss is only armed from the drawer's own header bar (not the content area), so
  // dragging inside a select/input/button never gets mistaken for a close gesture.
  const handleDragStart = (e) => {
    startXRef.current = e.touches ? e.touches[0].clientX : e.clientX;
    setDragging(true);
  };
  const handleDragMove = (e) => {
    if (!dragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setDragX(Math.max(0, x - startXRef.current));
  };
  const handleDragEnd = () => {
    if (!dragging) return;
    if (dragX > DRAG_CLOSE_THRESHOLD) {
      closeDrawer();
    } else {
      setDragging(false);
      setDragX(0);
    }
  };

  const panelTransform = entered ? `translateX(${dragX}px)` : 'translateX(100%)';

  return createPortal(
    <>
      <button
        type="button" onClick={openDrawer} aria-label={t('layout.openControls')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: '2.25rem', height: '2.25rem',
          padding: 0, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
          color: 'var(--color-text-main)', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <SlidersHorizontal size={18} />
      </button>
      {/* Deliberately always mounted, hidden rather than removed. Most modules keep a hidden
          <input type="file"> for CSV import among these controls; if closing the drawer unmounted
          them, that input would be torn out of the DOM while the OS file picker was still open and
          its change event would never fire -- silently breaking every import on mobile. */}
        <div
          onClick={closeDrawer}
          aria-hidden={!open}
          style={{
            position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end',
            backgroundColor: 'var(--color-overlay)', opacity: entered ? 1 : 0, transition: `opacity ${TRANSITION_MS}ms ease`,
            visibility: open ? 'visible' : 'hidden',
            pointerEvents: open ? 'auto' : 'none',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(85vw, 340px)', height: '100%', backgroundColor: 'var(--color-bg-card)', boxShadow: 'var(--shadow-lg)',
              display: 'flex', flexDirection: 'column', overflowY: 'auto',
              transform: panelTransform, transition: dragging ? 'none' : `transform ${TRANSITION_MS}ms ease`,
            }}
          >
            <div
              onTouchStart={handleDragStart} onTouchMove={handleDragMove} onTouchEnd={handleDragEnd}
              onPointerDown={handleDragStart} onPointerMove={handleDragMove} onPointerUp={handleDragEnd} onPointerCancel={handleDragEnd}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem',
                borderBottom: '1px solid var(--color-border)', flexShrink: 0, cursor: 'grab', touchAction: 'pan-y',
              }}
            >
              <span style={{ fontWeight: '700', fontSize: '1rem' }}>{t('layout.pageControls')}</span>
              <button type="button" onClick={closeDrawer} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            {/* Any action button in here (Add, Save, Import, Export, Confirm...) finishes the job it
                was opened for, so dismiss the drawer -- otherwise it stays parked on top of the
                dialog or table the button just produced. Selects and inputs are left alone: those
                are settings the user adjusts in place, often several before leaving. */}
            <div
              className="header-portal-drawer-content"
              onClick={(e) => { if (e.target.closest('button')) closeDrawer(); }}
              style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
            >
              {children}
            </div>
          </div>
        </div>
    </>,
    headerActionsEl
  );
};

export default HeaderActionsPortal;
